import {IMessageComponent} from '@/components/Chat/ChatMessage/ChatMessage';
import styles from './ImageMessage.module.scss'
import generalStyle from '../ChatMessage.module.scss'
import React, { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import {show as showImage} from '@/components/Modal/ImageViewer/utils';
import {getChatId} from '@/utils/chats';
import {getLastTime} from '@/utils/dateutils';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';
import { saveAs } from 'file-saver';
import { ApiProgressEvent } from '@gry-dmitrij/api-request';
import useGetNames from '@/hooks/useGetNames';
import ReplyMessage from '@/components/Chat/ChatMessage/ReplyMessage/ReplyMessage';
import {useAppSelector} from "@/hooks/appHook";
import {getActiveChatId, getChat} from "@/store/chats/chats";
import { downloadFileWithProgress } from '@/api/upload.ts';
import { getFileExtension, getMimeType } from '@/utils/file.ts';
import CirclePreloader from '@/components/Primitive/CirclePreloader/CirclePreloader.tsx';
import globalEvents from '@/services/GlobalEvents/GlobalEvents.ts';
import { Events } from '@/services/GlobalEvents/constants.ts';
import { isImageMessage } from './predicates.ts';

const ImageMessage = ({msg, incoming}: IMessageComponent) => {
  const {forwardedName, fromName} = useGetNames({message: msg, incoming})
  const activeChatId = useAppSelector(getActiveChatId)
  const activeChat = useAppSelector(getChat(activeChatId + ''))
  const [progress, setProgress] = useState(0)

  const downloadProgress = useCallback((progressEvent: ApiProgressEvent) => {
    setProgress(Math.round((progressEvent.progress || 0) * 100))
  }, [])

  const downloadImage = useCallback(async (messageId: string) => {
    if (messageId !== msg.id || !isImageMessage(msg)) {
      return
    }
    const {data} = await downloadFileWithProgress({
      url: msg.image.url,
      onDownloadProgress: downloadProgress,
    })
    setTimeout(() => {
      setProgress(0)
    }, 500)
    const fileExt = getFileExtension(msg.image.url)
    const mimeType = getMimeType(fileExt)
    const blob = new Blob([data], mimeType ? {type: mimeType} : undefined)
    saveAs(blob, 'image.' + fileExt)
  }, [msg, downloadProgress])

  useEffect(() => {
    globalEvents.addEventListener(Events.loadImageMessage, downloadImage)
    return () => {
      globalEvents.removeEventListener(Events.loadImageMessage, downloadImage)
    }
  }, [downloadImage]);

  if (!isImageMessage(msg)) {
    return null
  }

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (msg.status === 'sending') {
      return
    }
    e.stopPropagation()
    showImage(msg)
  }

  return <div
    className={classNames(
      styles.imageWrapper,
      incoming && styles.incoming,
      msg.status === 'sending' && styles.notActive
    )}
    onClick={handleClick}>
    {msg.forwardedFrom && <FormattedMessage id={'forwarded_from'}>
      {txt => <p className={styles.forwardedFrom}>{txt} {forwardedName || getChatId(msg.forwardedFrom || '')}</p>}
    </FormattedMessage>
    }
    {!!msg.replyMessage && <div className={styles.replyWrapper}><ReplyMessage message={msg.replyMessage}/></div>}
    {!!progress && <CirclePreloader
      className={styles.preloader}
      progress={progress}
    />}
    <img
      className={classNames(styles.image, msg.status === 'sending' && styles.sending)}
      src={msg.image.thumbnail}
      alt=""
    />
    <div className={styles.text}>
      <div className={styles.nick}>{fromName}</div>
      <div className={styles.right}>
        <div className={styles.time}>
          {activeChat?.pin?.uid === msg.id && activeChat.pin.visible
            && <i className={classNames("chat-pin", styles.chatPin)} />}
          {getLastTime(msg.timestamp)}
        </div>
        {!incoming && (msg.status === 'sent' || msg.status === 'received')
          ? <i className={classNames(
            "chat-check",
            generalStyle.tick,
            msg.status === 'sent' ? styles.sent : null)}/> : null}
        {!incoming && msg.status === 'displayed' ? <i className={classNames(
          'chat-double-check seen',
          generalStyle.tick
        )}/> : null}
        {!incoming && msg.status === 'sending' ? <i className={classNames(
          "chat-clock",
          generalStyle.tick,
          styles.sent)}/> : null}
      </div>
    </div>
  </div>
}

export default ImageMessage
